import React, { useState, useEffect, useRef } from "react";
import { Slider } from "@mui/material";
import { SunIcon } from "../../components/common/Icons/Icons";
import ModalShell from "../../components/common/Modals/ModalShell";
import "./LightsDialog.css";
import Command from "../../classes/command.class";
import { lightsDebounce } from "./debounceTimes";

interface LightsDialogProps {
    open: boolean;
    onClose: () => void;
    command: Command;
    initialRgb: { r: number; g: number; b: number };
    initialBrightness: number;
    initialPattern: number;
    showRdtColours?: boolean;
    showBrightness?: boolean;
    showPatterns?: boolean;
}

// ROYGBIV + White color definitions
const COLORS = [
    { name: "Red", rgb: { r: 255, g: 0, b: 0 } },
    { name: "Orange", rgb: { r: 255, g: 165, b: 0 } },
    { name: "Yellow", rgb: { r: 255, g: 255, b: 0 } },
    { name: "Green", rgb: { r: 0, g: 255, b: 0 } },
    { name: "Blue", rgb: { r: 0, g: 0, b: 255 } },
    { name: "Indigo", rgb: { r: 75, g: 0, b: 130 } },
    { name: "Violet", rgb: { r: 238, g: 130, b: 238 } },
    { name: "White", rgb: { r: 255, g: 255, b: 255 } },
];

const PATTERNS = [
    { name: "Solid", value: 0 },
    { name: "Fade In", value: 1 },
    { name: "Blinking", value: 2 },
    { name: "Spectrum", value: 3 },
];

function valuetext(value: number) {
    return `${((value / 255) * 100).toFixed(0)}%`;
}

const LightsDialog: React.FC<LightsDialogProps> = ({
    open,
    onClose,
    command,
    initialRgb,
    initialBrightness,
    initialPattern,
    showRdtColours = true,
    showBrightness = true,
    showPatterns = true,
}) => {
    const [rgb, setRgb] = useState<{ r: number; g: number; b: number }>(initialRgb);
    const [brightness, setBrightness] = useState<number>(initialBrightness);
    const [pattern, setPattern] = useState<number>(initialPattern);
    const [selectedColorIndex, setSelectedColorIndex] = useState<number | null>(7); // Default to White (index 7)
    const [selectedPattern, setSelectedPattern] = useState<number | null>(initialPattern);
    const [pendingColorIndex, setPendingColorIndex] = useState<number | null>(null); // Track which color is pending
    const [pendingPattern, setPendingPattern] = useState<number | null>(null); // Track which pattern is pending

    // Track user changes for debouncing
    const [lastUserRGB, setLastUserRGB] = useState<{ r: number; g: number; b: number } | null>(null);
    const [lastUserBrightness, setLastUserBrightness] = useState<number | null>(null);
    const [lastUserPattern, setLastUserPattern] = useState<number | null>(null);
    
    // Track retry attempts for commands that aren't echoed back
    const rgbRetryCountRef = useRef<number>(0);
    const brightnessRetryCountRef = useRef<number>(0);
    const patternRetryCountRef = useRef<number>(0);
    
    // Helper function to calculate color distance (Euclidean distance in RGB space)
    const getColorDistance = (color1: { r: number; g: number; b: number }, color2: { r: number; g: number; b: number }): number => {
        const dr = color1.r - color2.r;
        const dg = color1.g - color2.g;
        const db = color1.b - color2.b;
        return Math.sqrt(dr * dr + dg * dg + db * db);
    };

    // Update local state when props change and reset tracking variables when values match (settings echo)
    useEffect(() => {
        // Reset and update RGB - matches temperature pattern exactly
        if (lastUserRGB !== null && 
            initialRgb.r === lastUserRGB.r && 
            initialRgb.g === lastUserRGB.g && 
            initialRgb.b === lastUserRGB.b) {
            // Success - RGB was echoed back
            setLastUserRGB(null);
            rgbRetryCountRef.current = 0;
            setPendingColorIndex(null);
            // Find the matching color index
            const matchingIndex = COLORS.findIndex(
                c => c.rgb.r === initialRgb.r && c.rgb.g === initialRgb.g && c.rgb.b === initialRgb.b
            );
            if (matchingIndex !== -1) {
                setSelectedColorIndex(matchingIndex);
            }
        } else if (pendingColorIndex === null) {
            // Only update RGB and selectedColorIndex if we're not waiting for a pending color
            setRgb(initialRgb);
            // Find the closest matching color
            let closestIndex = 7; // Default to White
            let closestDistance = getColorDistance(initialRgb, COLORS[7].rgb);
            COLORS.forEach((color, index) => {
                const distance = getColorDistance(initialRgb, color.rgb);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });
            setSelectedColorIndex(closestIndex);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialRgb, open]);

    useEffect(() => {
        // Reset and update brightness - matches temperature pattern exactly
        if (lastUserBrightness !== null && initialBrightness === lastUserBrightness) {
            setLastUserBrightness(null);
            brightnessRetryCountRef.current = 0;
        }
        setBrightness(initialBrightness);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialBrightness, open]);

    useEffect(() => {
        // Reset and update pattern - matches temperature pattern exactly
        if (lastUserPattern !== null && initialPattern === lastUserPattern) {
            // Success - pattern was echoed back
            setLastUserPattern(null);
            patternRetryCountRef.current = 0;
            setPendingPattern(null);
            setPattern(initialPattern);
            setSelectedPattern(initialPattern);
        } else if (pendingPattern === null) {
            // Only update pattern if we're not waiting for a pending pattern
            setPattern(initialPattern);
            setSelectedPattern(initialPattern);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPattern, open]);

    // Find closest matching color from RGB settings
    useEffect(() => {
        if (open) {
            // Find the closest color by calculating distance to each color
            let closestIndex = 7; // Default to White
            let closestDistance = getColorDistance(initialRgb, COLORS[7].rgb);

            COLORS.forEach((color, index) => {
                const distance = getColorDistance(initialRgb, color.rgb);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });

            setSelectedColorIndex(closestIndex);
            setSelectedPattern(initialPattern);
            // Reset pending state when dialog opens
            setPendingColorIndex(null);
            setPendingPattern(null);
            setLastUserPattern(null);
            setLastUserRGB(null);
        }
    }, [open, initialRgb, initialPattern]);

    // Combined effect to send RGB and brightness together with debounce - matches temperature pattern exactly
    useEffect(() => {
        const shouldSendRGB = lastUserRGB !== null && 
            rgb.r === lastUserRGB.r && 
            rgb.g === lastUserRGB.g && 
            rgb.b === lastUserRGB.b;
        const shouldSendBrightness = lastUserBrightness !== null && 
            brightness === lastUserBrightness;

        // Reset after 10 failed attempts
        if (shouldSendRGB && rgbRetryCountRef.current >= 10) {
            setLastUserRGB(null);
            rgbRetryCountRef.current = 0;
            setPendingColorIndex(null);
            setRgb(initialRgb);
            // Reset to initial color
            let closestIndex = 7; // Default to White
            let closestDistance = getColorDistance(initialRgb, COLORS[7].rgb);
            COLORS.forEach((color, index) => {
                const distance = getColorDistance(initialRgb, color.rgb);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });
            setSelectedColorIndex(closestIndex);
            return;
        }

        if (shouldSendBrightness && brightnessRetryCountRef.current >= 10) {
            setLastUserBrightness(null);
            brightnessRetryCountRef.current = 0;
            setBrightness(initialBrightness);
            return;
        }

        if (shouldSendRGB || shouldSendBrightness) {
            // Calculate delay: lightsDebounce * (retryCount + 1) for exponential backoff
            const rgbDelay = shouldSendRGB ? lightsDebounce * (rgbRetryCountRef.current + 1) : 0;
            const brightnessDelay = shouldSendBrightness ? lightsDebounce * (brightnessRetryCountRef.current + 1) : 0;
            const delay = Math.max(rgbDelay, brightnessDelay);

            const timer = setTimeout(() => {
                const settingsToSend: any = {};

                // Include RGB values (use lastUserRGB if available, otherwise current rgb)
                if (shouldSendRGB && lastUserRGB) {
                    settingsToSend.setRDTred = lastUserRGB.r;
                    settingsToSend.setRDTgreen = lastUserRGB.g;
                    settingsToSend.setRDTblue = lastUserRGB.b;
                    rgbRetryCountRef.current += 1;
                } else {
                    // Use current RGB values if brightness is changing
                    settingsToSend.setRDTred = rgb.r;
                    settingsToSend.setRDTgreen = rgb.g;
                    settingsToSend.setRDTblue = rgb.b;
                }

                // Include brightness value (use lastUserBrightness if available, otherwise current brightness)
                if (shouldSendBrightness && lastUserBrightness !== null) {
                    settingsToSend.setRDTbright = lastUserBrightness;
                    brightnessRetryCountRef.current += 1;
                } else {
                    // Use current brightness if RGB is changing
                    settingsToSend.setRDTbright = brightness;
                }

                command.updateSettings(settingsToSend);
            }, delay);

            return () => clearTimeout(timer);
        }
    }, [lastUserRGB, rgb, lastUserBrightness, brightness, initialRgb, initialBrightness, command]);

    // Effect to send pattern separately with debounce - matches temperature pattern exactly
    // Only runs when lastUserPattern changes (user clicked pattern), not when pattern state updates
    useEffect(() => {
        if (lastUserPattern !== null && pattern === lastUserPattern) {
            // Reset after 10 failed attempts
            if (patternRetryCountRef.current >= 10) {
                setLastUserPattern(null);
                patternRetryCountRef.current = 0;
                setPendingPattern(null);
                setPattern(initialPattern);
                setSelectedPattern(initialPattern);
                return;
            }

            // Calculate delay: lightsDebounce * (retryCount + 1) for exponential backoff
            const delay = lightsDebounce * (patternRetryCountRef.current + 1);
            
            const timer = setTimeout(() => {
                command.updateSettings({
                    setRDTpattern: lastUserPattern,
                });
                patternRetryCountRef.current += 1;
            }, delay);

            return () => clearTimeout(timer);
        }
    }, [lastUserPattern, pattern, initialPattern, command]);

    const handleColorClick = (colorIndex: number) => {
        const color = COLORS[colorIndex];
        // Disable all color buttons and show loading state
        setPendingColorIndex(colorIndex);
        setRgb(color.rgb); // Update RGB state so useEffect can trigger
        setLastUserRGB(color.rgb);
        rgbRetryCountRef.current = 0;
        // Don't update selectedColorIndex yet - wait for success
    };

    const handleBrightnessChange = (newBrightness: number) => {
        setBrightness(newBrightness);
        setLastUserBrightness(newBrightness);
        brightnessRetryCountRef.current = 0;
    };

    const handlePatternClick = (patternValue: number) => {
        // Disable all pattern buttons and show loading state
        setPendingPattern(patternValue);
        setPattern(patternValue); // Update pattern state so useEffect can trigger
        setLastUserPattern(patternValue);
        patternRetryCountRef.current = 0;
        // Don't update selectedPattern yet - wait for success
    };

    return (
        <ModalShell open={open} onClose={onClose} title="Light controls">
            {/* Color Buttons - only if RDT colours in settings */}
            {showRdtColours && (
                <div className="lights-colors">
                    {COLORS.map((color, index) => {
                        const isPending = pendingColorIndex === index;
                        const isSelected = selectedColorIndex === index && pendingColorIndex === null;
                        // Disable buttons if there's any pending color operation
                        const isDisabled = pendingColorIndex !== null || lastUserRGB !== null;

                        return (
                            <button
                                key={color.name}
                                type="button"
                                className={`lights-color${isSelected ? " selected" : ""}`}
                                onClick={() => handleColorClick(index)}
                                disabled={isDisabled}
                                aria-label={color.name}
                                aria-pressed={isSelected}
                            >
                                {isPending ? (
                                    <span className="spinner lights-spinner" aria-hidden="true"/>
                                ) : (
                                    <span
                                        className="lights-swatch"
                                        style={{ backgroundColor: `rgb(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b})` }}
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {showRdtColours && (showPatterns || showBrightness) && <hr className="lights-divider"/>}

            {/* Pattern Buttons - only if RDT pattern in settings */}
            {showPatterns && (
                <div className="segmented lights-patterns" role="group" aria-label="Pattern">
                    {PATTERNS.map((pattern) => {
                        const isPending = pendingPattern === pattern.value;
                        const isSelected = selectedPattern === pattern.value && pendingPattern === null;
                        // Disable buttons if there's any pending pattern operation
                        const isDisabled = pendingPattern !== null || lastUserPattern !== null;

                        return (
                            <button
                                key={pattern.value}
                                type="button"
                                className={isSelected ? "active" : undefined}
                                onClick={() => handlePatternClick(pattern.value)}
                                disabled={isDisabled}
                                aria-pressed={isSelected}
                            >
                                {isPending ? (
                                    <span className="spinner lights-spinner" aria-label={pattern.name}/>
                                ) : (
                                    pattern.name
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {showPatterns && showBrightness && <hr className="lights-divider"/>}

            {/* Brightness Slider - only if RDT brightness in settings */}
            {showBrightness && (
                <div className="lights-brightness">
                    <SunIcon/>
                    <Slider
                        className="lights-slider"
                        aria-label="Brightness"
                        valueLabelDisplay="auto"
                        min={0}
                        max={255}
                        valueLabelFormat={valuetext}
                        value={brightness}
                        onChange={(e, value) => handleBrightnessChange(Number(value))}
                    />
                </div>
            )}
        </ModalShell>
    );
};

export default LightsDialog;

